import { cn } from '@/lib/utils';
import { Skeleton } from '@/components/common/SkeletonLoader';
import { ChevronUp, ChevronDown, ChevronsUpDown, ChevronLeft, ChevronRight } from 'lucide-react';

export interface ColumnDef<T> {
  /** Unique key for the column, used as sortBy value */
  key: string;
  /** Display header text */
  header: string;
  /** Whether this column is sortable */
  sortable?: boolean;
  /** Whether to right-align (for numeric columns) */
  align?: 'left' | 'right';
  /** Custom cell renderer */
  render?: (row: T) => React.ReactNode;
}

export interface DrillDownTableProps<T> {
  /** Column definitions */
  columns: ColumnDef<T>[];
  /** Row data */
  data: T[];
  /** Total count for pagination */
  totalCount: number;
  /** Current page (1-indexed) */
  page: number;
  /** Items per page */
  pageSize: number;
  /** Current sort column */
  sortBy?: string;
  /** Current sort direction */
  sortDir?: 'asc' | 'desc';
  /** Sort change handler */
  onSort?: (sortBy: string, sortDir: 'asc' | 'desc') => void;
  /** Page change handler */
  onPageChange?: (page: number) => void;
  /** Whether data is loading */
  loading?: boolean;
  /** Message to display when no data */
  emptyMessage?: string;
  /** Optional row key extractor */
  rowKey?: (row: T) => string;
  /** Optional row click handler */
  onRowClick?: (row: T) => void;
  /** Optional row class name callback for conditional row styling */
  rowClassName?: (row: T) => string | undefined;
  /** Additional CSS classes */
  className?: string;
}

/**
 * Sort indicator icon for column headers.
 */
function SortIcon({ active, direction }: { active: boolean; direction?: 'asc' | 'desc' }) {
  if (!active) {
    return <ChevronsUpDown className="h-3 w-3 text-[#9A9A9A]" aria-hidden="true" />;
  }
  if (direction === 'asc') {
    return <ChevronUp className="h-3 w-3 text-[#6B4FBB]" aria-hidden="true" />;
  }
  return <ChevronDown className="h-3 w-3 text-[#6B4FBB]" aria-hidden="true" />;
}

/**
 * Shared drill-down table component with server-side pagination and sorting.
 * Used across all analytics metric drill-down views.
 */
export function DrillDownTable<T>({
  columns,
  data,
  totalCount,
  page,
  pageSize,
  sortBy,
  sortDir = 'desc',
  onSort,
  onPageChange,
  loading = false,
  emptyMessage = 'No items to display',
  rowKey,
  onRowClick,
  rowClassName,
  className,
}: DrillDownTableProps<T>) {
  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));
  const startItem = totalCount === 0 ? 0 : (page - 1) * pageSize + 1;
  const endItem = Math.min(page * pageSize, totalCount);

  function handleSort(columnKey: string) {
    if (!onSort) return;
    if (sortBy === columnKey) {
      onSort(columnKey, sortDir === 'asc' ? 'desc' : 'asc');
    } else {
      onSort(columnKey, 'desc');
    }
  }

  if (loading) {
    return (
      <div className={cn('bg-white border border-neutral-200 rounded-lg overflow-hidden', className)} aria-busy="true">
        <div className="overflow-x-auto">
          <table className="w-full border-collapse">
            <thead>
              <tr className="bg-[#F7F7F7] border-b border-neutral-200">
                {columns.map((col) => (
                  <th
                    key={col.key}
                    className="px-4 py-3 text-left"
                  >
                    <Skeleton className="h-3 w-16" />
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {Array.from({ length: 5 }).map((_, i) => (
                <tr key={i} className="border-b border-[#EFEFEF]">
                  {columns.map((col) => (
                    <td key={col.key} className="px-4 py-3">
                      <Skeleton className="h-4 w-24" />
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  }

  if (data.length === 0 && totalCount === 0) {
    return (
      <div className={cn('bg-white border border-neutral-200 rounded-lg p-8 text-center', className)}>
        <p className="text-sm text-[#555555]">{emptyMessage}</p>
      </div>
    );
  }

  return (
    <div className={cn('bg-white border border-neutral-200 rounded-lg overflow-hidden', className)}>
      <div className="overflow-x-auto">
        <table className="w-full border-collapse">
          <thead>
            <tr className="bg-[#F7F7F7] border-b border-neutral-200">
              {columns.map((col) => (
                <th
                  key={col.key}
                  className={cn(
                    'px-4 py-3 text-[11px] font-semibold uppercase tracking-[0.1em] text-[#555555] whitespace-nowrap',
                    col.align === 'right' ? 'text-right' : 'text-left',
                  )}
                  aria-sort={
                    sortBy === col.key
                      ? sortDir === 'asc'
                        ? 'ascending'
                        : 'descending'
                      : undefined
                  }
                >
                  {col.sortable && onSort ? (
                    <button
                      type="button"
                      className="inline-flex items-center gap-1 cursor-pointer hover:text-[#6B4FBB] select-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#6B4FBB] focus-visible:ring-offset-1 rounded"
                      onClick={() => handleSort(col.key)}
                    >
                      {col.header}
                      <SortIcon
                        active={sortBy === col.key}
                        direction={sortBy === col.key ? sortDir : undefined}
                      />
                    </button>
                  ) : (
                    <span className="inline-flex items-center gap-1">
                      {col.header}
                    </span>
                  )}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {data.map((row, index) => {
              const key = rowKey ? rowKey(row) : String(index);
              return (
                <tr
                  key={key}
                  className={cn(
                    'border-b border-[#EFEFEF] hover:bg-[#EEE9F9] transition-colors',
                    onRowClick && 'cursor-pointer',
                    rowClassName?.(row)
                  )}
                  onClick={onRowClick ? () => onRowClick(row) : undefined}
                  {...(onRowClick ? {
                    tabIndex: 0,
                    role: 'button',
                    onKeyDown: (e: React.KeyboardEvent<HTMLTableRowElement>) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        onRowClick(row);
                      }
                    },
                  } : {})}
                >
                  {columns.map((col) => (
                    <td
                      key={col.key}
                      className={cn(
                        'px-4 py-3 text-sm text-[#111111]',
                        col.align === 'right' ? 'text-right tabular-nums' : 'text-left'
                      )}
                    >
                      {col.render
                        ? col.render(row)
                        : String((row as Record<string, unknown>)[col.key] ?? '—')}
                    </td>
                  ))}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Pagination controls */}
      {totalCount > pageSize && (
        <div className="flex items-center justify-between px-4 py-3 border-t border-neutral-200 bg-[#F7F7F7]">
          <p className="text-sm text-[#555555]">
            Showing {startItem}–{endItem} of {totalCount}
          </p>
          <div className="flex items-center gap-2">
            <button
              onClick={() => onPageChange?.(page - 1)}
              disabled={page <= 1}
              className="inline-flex items-center justify-center h-8 w-8 rounded border border-neutral-200 bg-white text-[#555555] hover:border-[#6B4FBB] hover:text-[#6B4FBB] disabled:opacity-50 disabled:pointer-events-none transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#6B4FBB] focus-visible:ring-offset-2"
              aria-label="Previous page"
            >
              <ChevronLeft className="h-4 w-4" />
            </button>
            <span className="text-sm text-[#555555] tabular-nums">
              Page {page} of {totalPages}
            </span>
            <button
              onClick={() => onPageChange?.(page + 1)}
              disabled={page >= totalPages}
              className="inline-flex items-center justify-center h-8 w-8 rounded border border-neutral-200 bg-white text-[#555555] hover:border-[#6B4FBB] hover:text-[#6B4FBB] disabled:opacity-50 disabled:pointer-events-none transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#6B4FBB] focus-visible:ring-offset-2"
              aria-label="Next page"
            >
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default DrillDownTable;
